// פונקציות ניקוי טקסט מרוכזות

// ====================================
// === פונקציות בסיסיות לניקוי טקסט ===
// ====================================

/**
 * מסירה תווים מיוחדים מטקסט
 * @param text - טקסט קלט
 * @param withNonLetters - האם להסיר תווים שאינם אותיות עבריות (ברירת מחדל: true)
 * @returns טקסט נקי מתווים מיוחדים וסוגריים. אם withNonLetters=true, משאיר רק אותיות עבריות בסיסיות ורווחים
 */
export const removeNonLetters = (
  text: string,
  withNonLetters = true
): string => {
  const words = text.replace(/(\([^)]*\)|\n)/g, '');
  if (!withNonLetters) return words;
  // משאיר רק אותיות עבריות בסיסיות (א-ת) ואותיות מיוחדות (ױײ׳״) ורווחים
  return words.replace(/[^\u05D0-\u05EA\u05F0-\u05F4\s]/g, '');
};

/**
 * מסיר רווחים מיותרים ומנרמל אותם
 */
export const normalizeSpaces = (text: string): string =>
  text.replace(/\s+/g, ' ').trim();

/**
 * משאיר רק אותיות עבריות ורווחים
 */
export const keepOnlyHebrewAndSpaces = (text: string): string =>
  text.replace(/[^\u0590-\u05FF\s]/g, '');

/**
 * מסיר את כל סימני הפיסוק מהטקסט
 */
export const removePunctuation = (text: string): string =>
  text.replace(/[.,;:!?׃]/g, '');

/**
 * מסיר תווים ספציפיים לא רצויים מהטקסט
 * מתחיל עם התו | (pipe) ויכול להתרחב בעתיד
 * 
 * @param text - טקסט קלט
 * @returns טקסט ללא התווים הלא רצויים
 * 
 * @example
 * removeUnwantedCharacters("מָה רַבּוּ מַעֲשֶׂיךָ | יְהֹוָה")
 * // -> "מָה רַבּוּ מַעֲשֶׂיךָ יְהֹוָה"
 */
export const removeUnwantedCharacters = (text: string): string => {
  return text
    .replace(/\|/g, ' ') // מסיר את התו | (pipe) ומחליף אותו ברווח
    .replace(/\s+/g, ' ') // מנקה רווחים כפולים שנוצרו
    .trim(); // מסיר רווחים בתחילת ובסוף
};

// ===================================
// === פונקציות לטיפול בתגיות HTML ===
// ===================================

/**
 * מסיר תגיות HTML מטקסט נתון ומוודא שיש רווחים מתאימים בין מילים
 * @param htmlString - טקסט שמכיל תגיות HTML
 * @returns טקסט נקי ללא תגיות HTML עם רווחים מתאימים
 */
export const removeHtmlTags = (htmlString: string) => {
  return htmlString
    // מחליף תגיות HTML ברווח
    .replace(/<[^>]*>/g, ' ')
    // מנקה רווחים מיותרים
    .replace(/\s+/g, ' ')
    // מסיר רווחים בתחילת ובסוף הטקסט
    .trim();
}

/**
 * מסיר תגיות HTML מטקסט אבל שומר על מבנה המילים 
 * (לא יוצר פיצולים לא רצויים כמו בפונקציה הרגילה)
 */
export const removeHtmlTagsButKeepWordStructure = (htmlString: string): string => {
  return htmlString
    // מסיר תגיות שלא אמורות לפצל מילים (span, em, strong וכו')
    .replace(/<\/?(?:span|em|strong|i|b|u)[^>]*>/g, '')
    // מחליף תגיות שאמורות לפצל (div, p, br) ברווח
    .replace(/<(?:div|p|br)[^>]*>/g, ' ')
    .replace(/<\/(?:div|p)>/g, ' ')
    // מסיר שאר התגיות ברווח
    .replace(/<[^>]*>/g, ' ')
    // מנקה רווחים מיותרים
    .replace(/\s+/g, ' ')
    // מסיר רווחים בתחילת ובסוף הטקסט
    .trim();
};

export const removeSpanAndP = (text: string): string =>
  text.replace(/<\/?(?:span|p)>/g, ' ');

export const removeSmallTags = (text: string): string =>
  text.replace(/<small>.*?<\/small>/g, ' ');

export const unwrapBigTags = (text: string): string =>
  text.replace(/<big>(.*?)<\/big>/g, '$1');

export const removeOtherTagsExceptBr = (text: string): string =>
  text.replace(/<(?!br\s*\/?)[^>]+>/g, ' ');

/**
 * מסירה סימני הערת שוליים שהגיעו מייצוא ה-InDesign של המשנה הסדורה
 * דוגמה: '<span class="NoteMarker">387</span>'
 *
 * הסימנים האלה הם מספרי הערות מהספר המודפס, לא טקסט לקריאה.
 * בלעדי ההסרה המספר גם מוצג בתוך המשנה וגם נספר כמילה ב-originalWordsArray,
 * בעוד ש-cleanWordsArray (שמשמש להשוואה מול ההקלטה) לא כולל אותו,
 * ולכן כל המילים שאחריו מקבלות את הסטטוס של המילה שלפניהן.
 *
 * @param text - טקסט קלט עם תגיות HTML
 * @returns טקסט ללא תגיות NoteMarker
 *
 * @example
 * removeNoteMarkerSpans('<span class="x-25">"תִּזְרֵם</span><span class="NoteMarker">387</span>')
 * // -> '<span class="x-25">"תִּזְרֵם</span>'
 */
export const removeNoteMarkerSpans = (text: string): string =>
  text.replace(
    /<span[^>]*\bclass="[^"]*\bNoteMarker\b[^"]*"[^>]*>[\s\S]*?<\/span>/gi,
    ''
  );

// =====================================
// === פונקציות לטיפול בתגיות x-24/25 ===
// =====================================

/**
 * מטפל בתגיות span עם קלאס x-25 (ציטוטים) - מחבר אותם למילה הקודמת
 * דוגמה: "מִ<span class='x-25'>"זִבְחֵי מֵתִים"</span>" -> "מִזִבְחֵי מֵתִים"
 */
export const handleQuoteSpansX25 = (text: string): string => {
  return text.replace(/(\S+)\s*<span[^>]*class="[^"]*x-25[^"]*"[^>]*>\s*"?([^<"]*?)"?\s*<\/span>/gi, '$1$2');
};

/**
 * מסיר תגיות span עם קלאס x-25 (ציטוטים) אבל משאיר את התוכן מחובר למילה הקודמת
 * דוגמה: 'מִ<span class="x-25">"זִבְחֵי מֵתִים"</span>' -> 'מִזִבְחֵי מֵתִים'
 */
export const removeQuoteSpansForDisplay = (text: string): string => {
  // דפוס מורכב יותר שתופס מקרים שונים של ציטוטים
  return text
    // דפוס 1: מילה + רווח + span עם x-25 ומרכאות
    .replace(/(\S+)\s+<span[^>]*class="[^"]*x-25[^"]*"[^>]*>\s*"?([^<"]*?)"?\s*<\/span>/gi, '$1$2')
    // דפוס 2: מילה צמודה לspan עם x-25 ומרכאות
    .replace(/(\S+)<span[^>]*class="[^"]*x-25[^"]*"[^>]*>\s*"?([^<"]*?)"?\s*<\/span>/gi, '$1$2')
    // דפוס 3: כל span עם x-25 (fallback)
    .replace(/<span[^>]*class="[^"]*x-25[^"]*"[^>]*>\s*"?([^<"]*?)"?\s*<\/span>/gi, '$1');
};

/**
 * מסיר span x-24 ומשאיר רק את הפיסוק צמוד למילה שלפני, ללא רווחים מיותרים
 */
export const removeX24SpanAndAttachPunctuation = (text: string): string =>
  text.replace(/(\S)\s*<span[^>]*class="x-24"[^>]*>\s*([^<]*)\s*<\/span>/g, '$1$2');

/**
 * מסיר span x-24 ומוסיף רווח לפני הפיסוק (במקום לחבר ישירות)
 * זה פותר את הבעיה של מילים מוצמדות במשניות
 */
export const removeX24SpanAndAddSpaceBeforePunctuation = (text: string): string =>
  text.replace(/(\S)\s*<span[^>]*class="x-24"[^>]*>\s*([^<]*)\s*<\/span>/g, '$1 $2');

// =====================================
// === פונקציות לטיפול בסימני פיסוק ===
// =====================================

/**
 * מצמיד פיסוק למילה שלפניו ומוסיף רווח אחריו
 * מוסיף רווח אחרי הפיסוק רק אם הוא לא בסוף המחרוזת
 */
export const attachPunctuationToWord = (text: string): string =>
  text.replace(/(\S)\s+([.,;:!?])(\s|$)/g, '$1$2$3');

/**
 * מסיר רווחים מיותרים אחרי פיסוק ומוודא שיש רווח אחרי פיסוק במשניות
 * דוגמה: "לוֹמַר : תְּרוּמָה" -> "לוֹמַר: תְּרוּמָה"
 */
export const fixPunctuationSpacing = (text: string): string => {
  return text
    // מסיר רווחים לפני פיסוק
    .replace(/\s+([.,;:!?״׳"'])/g, '$1')
    // מוודא שיש רווח אחרי פיסוק (אם אין)
    .replace(/([.,;:!?״׳"'])([^\s])/g, '$1 $2');
};

export const removeQuotes = (text: string): string =>
  text.replace(/[״""]/g, '');

// =====================================
// === פונקציות לטיפול בסוגריים ===
// =====================================

export const removeSquareBrackets = (text: string): string =>
  text.replace(/\[[^\]]*\]/g, ' ');

export const removeOnlySquareBrackets = (text: string): string =>
  text.replace(/\[([^\]]*)\]/g, '$1');

export const removeParentheses = (text: string): string =>
  text.replace(/\([^)]*\)/g, ' ');

// =====================================
// === פונקציות לטיפול בטקסט עברי ===
// =====================================

export const splitMaqqaf = (text: string): string => {
  // מחליף מקפים עבריים ברווח בכל הטקסט (הן בתוך תגיות והן מחוצה להן)
  // אבל שומר על המקפים שבתוכן של תכונות (attributes) של התגיות
  // שומר על סימן "—" (em dash) כי הוא משמש לסימון הפסקות
  return text
    // מחליף מקפים בתוכן הטקסטואלי שבין תגיות (רק מקף עברי ומקף רגיל, לא em dash)
    .replace(/>([^<]*)</g, (match, content) => {
      const replacedContent = content.replace(/[־-]/g, ' ');
      return `>${replacedContent}<`;
    })
    // מחליף מקפים גם בטקסט שלא בתוך תגיות (אם יש)
    .replace(/^([^<]*?)(?=<|$)/g, (match) => match.replace(/[־-]/g, ' '))
    // מחליף מקפים בסוף הטקסט אחרי התגית האחרונה
    .replace(/>([^<]*)$/g, (match, content) => {
      const replacedContent = content.replace(/[־-]/g, ' ');
      return `>${replacedContent}`;
    });
};

export const removePaseq = (text: string): string =>
  text.replace(/׀/g, '');

export const removeSamekh = (text: string): string =>
  text.replace(/{ס}/g, '');

export const removePe = (text: string): string =>
  text.replace(/{פ}/g, '');

export const removeCantillation = (text: string): string =>
  text.replace(/[\u0591-\u05AF\u05BD]/g, '');

export const removeVowelPoints = (text: string): string =>
  text.replace(/[\u05B0-\u05C3]/g, '');

/**
 * מחליף Kamats Katan (ֳ) ב-Kamats רגיל (ָ) לפתרון בעיות תצוגה
 * Kamats Katan (U+05C7) יכול לגרום לבעיות רינדור בדפדפנים
 * 
 * @param text - טקסט קלט
 * @returns טקסט עם Kamats Katan מוחלף ב-Kamats רגיל
 * 
 * @example
 * replaceKamatsKatan("קׇמַץ קָטָן") -> "קָמַץ קָטָן"
 */
export const replaceKamatsKatan = (text: string): string =>
  text.replace(/\u05C7/g, '\u05B8');

/**
 * מחליף Holam Haser (ֺ) ב-Holam רגיל (ֹ) לפתרון בעיות תצוגה
 * Holam Haser (U+05BA) יכול לגרום לבעיות רינדור בדפדפנים
 * 
 * @param text - טקסט קלט
 * @returns טקסט עם Holam Haser מוחלף ב-Holam רגיל
 * 
 * @example
 * replaceHolamHaser("אֱלֺהֵינוּ") -> "אֱלֹהֵינוּ"
 */
export const replaceHolamHaser = (text: string): string =>
  text.replace(/\u05BA/g, '\u05B9');

/**
 * מחפש אותיות עבריות בודדות (עם ניקוד אפשרי) שיש להן רווח מלפניהן ומאחריהן ומצמיד אותן למילה שאחריהן
 * דוגמה: "קָרוּי וְ רֵאשִׁית" -> "קָרוּי וְרֵאשִׁית"
 */
export const attachSingleCharacterToPreviousWord = (text: string): string =>
  text.replace(/(\s)([\u05D0-\u05EA][\u05B0-\u05C3\u05F0-\u05F4]*)\s+(\S)/g, '$1$2$3');

// =====================================
// === פונקציות מורכבות ופייפליינים ===
// =====================================

/**
 * מטפל במרכאות בתוך תגיות x-25 - משאיר רווחים לפני ואחרי התגית ומנקה רווחים מיותרים בתוכה
 */
export const handleX25QuotationMarks = (text: string): string => {
  return text
    // מנקה רווחים מיותרים בתוך תגיות x-25
    .replace(/(<span[^>]*class="[^"]*x-25[^"]*"[^>]*>)\s*"?\s*/g, '$1"')
    .replace(/\s*"?\s*(<\/span>)/g, '"$1')
    // מוסיף רווח לפני ואחרי התגית (אבל לא לפני וי"ו החיבור)
    .replace(/(\S)(<span[^>]*class="[^"]*x-25[^"]*"[^>]*>)/g, '$1 $2')
    .replace(/(<\/span>)(\s*[^ו\s])/g, '$1 $2')
    // מתקן רווח כפול לפני וי"ו החיבור
    .replace(/\s+וְ\s+/g, ' וְ');
};

/**
 * מנקה רווחים מיותרים בתוך תגיות x-25, במיוחד לפני תגית הסגירה
 * דוגמה: '<span class="x-25">"טקסט" </span>' -> '<span class="x-25">"טקסט"</span>'
 */
export const cleanX25SpanSpaces = (text: string): string => {
  return text
    // מסיר רווחים מיותרים לפני תגית סגירה של x-25
    .replace(/(<span[^>]*class="[^"]*x-25[^"]*"[^>]*>[^<]*?)\s+(<\/span>)/g, '$1$2')
    // מסיר רווחים מיותרים אחרי תגית פתיחה של x-25 (אם יש)
    .replace(/(<span[^>]*class="[^"]*x-25[^"]*"[^>]*>)\s+/g, '$1');
};

/**
 * מסיר את המאפיין xml:lang="he-IL" מכל תגית span
 */
export const removeXmlLangFromSpans = (text: string): string =>
  text.replace(/(<span\b[^>]*?)\s+xml:lang="he-IL"/g, '$1');

// =====================================
// === פונקציות לטיפול במערכי טקסט ===
// =====================================

/**
 * מסיר איברים ריקים מהמערך (תגיות HTML ללא תוכן)
 * דוגמה: ["<p>טקסט</p>", "<p class=\"x-22\"></p>", "<p>עוד טקסט</p>"] 
 * -> ["<p>טקסט</p>", "<p>עוד טקסט</p>"]
 * 
 * @param textArray מערך של מחרוזות שעשויות להכיל תגיות HTML ריקות
 * @returns מערך ללא איברים ריקים
 */
export const removeEmptyHtmlElements = (textArray: string[]): string[] => {
  return textArray.filter(item => {
    // מסיר רווחים מיותרים ובודק אם נשאר תוכן אחרי הסרת תגיות HTML
    const cleanItem = item.trim().replace(/<[^>]*>/g, '').trim();
    return cleanItem.length > 0;
  });
};

// ====================================
// === פונקציות בסיסיות לניקוי טקסט ===
// ====================================

// Clean a string and return it as an array of words
export const getCleanStringAsArray = (
  str: string[],
  withNonLetters?: boolean
): string[] =>
  removeNonLetters(str?.join(' '), withNonLetters)
    .split(' ')
    .filter((el) => el && el !== '\n');

/**
 * מסיר כותרות מקוננות (פשוט, לא מושלם)
 */
export const removeNestedHeadings = (text: string): string =>
  text.replace(/<h([1-6])>[\s\S]*?<h[1-6]>[\s\S]*?<\/h[1-6]>[\s\S]*?<\/h\1>/gi, '');

/**
 * מחליף HTML entities ברווחים רגילים, אבל רק אם אין רווח כבר קיים לידם
 * כדי למנוע רווחים כפולים
 */
export const removeHtmlEntities = (text: string): string => {
  return text
    // אם יש רווח לפני או אחרי thinsp, מסיר את ה-thinsp בלבד
    .replace(/\s&thinsp;/g, ' ')
    .replace(/&thinsp;\s/g, ' ')
    .replace(/\s&thinsp;\s/g, ' ')
    // אם thinsp לבד (ללא רווחים), מחליף ברווח
    .replace(/&thinsp;/g, ' ')
    // אותו דבר עבור nbsp וentities אחרים
    .replace(/\s&nbsp;/g, ' ')
    .replace(/&nbsp;\s/g, ' ')
    .replace(/\s&nbsp;\s/g, ' ')
    .replace(/&nbsp;/g, ' ')
    // numeric entities
    .replace(/\s&#8201;/g, ' ')
    .replace(/&#8201;\s/g, ' ')
    .replace(/\s&#8201;\s/g, ' ')
    .replace(/&#8201;/g, ' ')
    .replace(/\s&#160;/g, ' ')
    .replace(/&#160;\s/g, ' ')
    .replace(/\s&#160;\s/g, ' ')
    .replace(/&#160;/g, ' ');
};

/**
 * פייפליין בסיסי לניקוי טקסט
 */
export function cleanTextPipeline(text: string): string {
  return normalizeSpaces(
    keepOnlyHebrewAndSpaces(
      removeCantillation(
        removePe(
          removeSamekh(
            removePaseq(
              replaceHolamHaser(
                replaceKamatsKatan(
                  splitMaqqaf(
                    removeOnlySquareBrackets(
                      removeOtherTagsExceptBr(
                        unwrapBigTags(
                          removeSmallTags(
                            removeHtmlEntities(
                              removeSpanAndP(text)
                            )
                          )
                        )
                      )
                    )
                  )
                )
              )
            )
          )
        )
      )
    )
  );
}


